import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ReviewFilters } from "@/domains/settings/features/reviews/components/review-filters";
import { SalonReviewFormDialog } from "@/domains/settings/features/reviews/components/salon-review-form-dialog";
import { SalonReviewList } from "@/domains/settings/features/reviews/components/salon-review-list";
import { useReviewFilterIntent } from "@/domains/settings/features/reviews/hooks/use-review-filter-intent";
import { useSalonReviews } from "@/domains/settings/features/reviews/hooks/use-salon-reviews";
import { useReviewUIStore } from "@/domains/settings/features/reviews/store/use-review-ui-store";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useSalonStore } from "@/lib/store/salon";
import { StarDistributionBar } from "../components/star-distribution-bar";
import { useSalonReviewStats } from "../hooks/use-salon-review-stats";

/**
 * Platform reviews detail page — in-app (storefront) reviews entered
 * manually from the dashboard. These are repliable only within the app
 * (they don't sync anywhere), so the reply filter is hidden and a "+
 * Create" CTA is surfaced (matches the small "Tao" button in the
 * wireframe).
 */
export function PlatformReviewsPage() {
  const { t } = useTranslation("reviews");
  const navigate = useNavigate();
  const { currentSalon } = useSalonStore();
  const { reviews } = useSalonReviews({ source: "MANUAL" });
  const { stats } = useSalonReviewStats("MANUAL");
  const { isCreateDialogOpen, isEditDialogOpen, editingReviewId, openCreateDialog } =
    useReviewUIStore();

  useReviewFilterIntent();

  const editingReview = reviews.find((review) => review.id === editingReviewId);

  return (
    <>
      <BaseNavigationBar
        title={t("platform.title")}
        subtitle={t("platform.subtitle")}
        backButton={<BackButton onBack={() => navigate("/customer-service")} />}
        trailingElements={[
          <Button
            key="create"
            onClick={openCreateDialog}
            className="px-3 sm:px-4"
            aria-label={t("platform.create")}
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("platform.create")}</span>
          </Button>,
        ]}
      />
      <PageContent>
        {currentSalon ? (
          <div className="space-y-6">
            <Card>
              <CardContent className="p-5">
                {stats ? (
                  <div className="grid md:grid-cols-[auto_1fr] gap-4 md:gap-8 items-center">
                    <div>
                      <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">
                        {stats.count ? stats.averageRating.toFixed(1) : "—"}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {stats.count}{" "}
                        {stats.count === 1 ? t("overview.review") : t("overview.reviews")}
                      </div>
                    </div>
                    <StarDistributionBar distribution={stats.distribution} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                )}
              </CardContent>
            </Card>

            <ReviewFilters showReplyFilter={false} />
            <SalonReviewList source="MANUAL" />
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">{t("overview.selectSalon")}</div>
        )}
      </PageContent>

      {isCreateDialogOpen && <SalonReviewFormDialog />}
      {isEditDialogOpen && editingReview && <SalonReviewFormDialog review={editingReview} />}
    </>
  );
}
